import { useCallback } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'

import {
  createConversation,
  createConversationReply,
  deleteConversation,
  deleteReplies,
  deleteReply,
  getConversation,
  pinReplies,
} from '@/services/conversation'
import { CustomStreamClient } from '@/services/customStreamClient'
import { getActivityByForeignIdTimes } from '@/services/getstream'

import { ResponseType } from './useDailyLimitToast'
import { useErrorBoundary } from './useErrorBoundary'
import { useFileUpload } from './useFileUpload'
import { useInvalidateQueries } from './useInvalidateQueries'
import { useUserInfo } from './useUserInfo'

import { MediaType } from '@/plugins/ugc-composer'

// eslint-disable-next-line @typescript-eslint/no-empty-function
export const useConversation = ({ onFileUploadComplete = () => {} } = {}) => {
  const { invalidateQueriesTimeout } = useInvalidateQueries()
  const { upload, progress, isUploading } = useFileUpload()
  const { data: userInfo } = useUserInfo()
  const { handleError } = useErrorBoundary()
  const history = useHistory()

  const {
    mutateAsync: onCreateConversation,
    isPending: mutationLoading,
    isError: mutationError,
  } = useMutation({
    mutationFn: createConversation,
    mutationKey: ['createConversation'],
    onError: (error: { response: ResponseType }) =>
      handleError(error, () => history.goBack()),
  })

  const {
    mutateAsync: onCreateConversationReply,
    isPending: isReplyMutationLoading,
    isError: replyMutationError,
  } = useMutation({
    mutationKey: ['createConversationReply'],
    mutationFn: createConversationReply,
    onError: (error: { response: ResponseType }) =>
      handleError(error, () => history.goBack()),
  })

  const {
    data: conversation,
    mutateAsync: onGetConversation,
    isPending: getMutationLoading,
    isError: getMutationError,
  } = useMutation({
    mutationKey: ['createConversationReply'],
    mutationFn: getConversation,
    onError: (error: { response: ResponseType }) => handleError(error),
  })

  const {
    mutateAsync: onDeleteConversation,
    isPending: isDeleteConversationMutationLoading,
    isError: onDeleteConversationMutationError,
  } = useMutation({
    mutationKey: ['deleteConversation'],
    mutationFn: deleteConversation,
    onError: (error: { response: ResponseType }) => handleError(error),
    onSettled: () => {
      invalidateQueriesTimeout()
    },
  })

  const {
    mutateAsync: onDeleteReplies,
    isPending: isDeleteRepliesLoading,
    isError: deleteRepliesError,
  } = useMutation({
    mutationFn: deleteReplies,
    mutationKey: ['onDeleteReplies'],
    onError: (error: { response: ResponseType }) => handleError(error),
  })

  const {
    mutateAsync: onDeleteReply,
    isPending: isDeleteReplyLoading,
    isError: deleteReplyError,
  } = useMutation({
    mutationKey: ['onDeleteReply'],
    mutationFn: deleteReply,
    onError: (error: { response: ResponseType }) => handleError(error),
    onSettled: () => {
      invalidateQueriesTimeout()
    },
  })

  const {
    mutateAsync: onPinReplies,
    isPending: isPinRepliesLoading,
    isError: pinRepliesError,
  } = useMutation({
    mutationFn: pinReplies,
    mutationKey: ['onPinReplies'],
    onError: (error: { response: ResponseType }) => handleError(error),
  })

  const uploadConversationMediaFile = useCallback(
    async (
      id: string,
      mediaUrl: string,
      mediaType: MediaType,
      description?: string
    ) => {
      if (!mediaUrl) return

      await fetch(mediaUrl)
        .then((res) => res.blob())
        .then((blob) => {
          const path =
            mediaType === 'image'
              ? `users/${userInfo.id}/conversation/${id}.${blob.type
                  .split('/')
                  .at(-1)}`
              : `users/${userInfo.id}/conversation-video/${id}`
          return upload({ fileName: path, file: blob, description })
        })
      onFileUploadComplete()
    },
    [onFileUploadComplete, userInfo.id, upload]
  )

  const getActivityId = async (
    client: CustomStreamClient,
    id: UUID,
    date: string
  ) => {
    const [activity] = await getActivityByForeignIdTimes(client, [
      {
        foreignID: id,
        time: date,
      },
    ])

    return activity?.id
  }

  return {
    onCreateConversation,
    onGetConversation,
    onCreateConversationReply,
    onDeleteReplies,
    onPinReplies,
    onDeleteReply,
    onDeleteConversation,
    pinRepliesError,
    isDeleteReplyLoading,
    isPinRepliesLoading,
    deleteReplyError,
    conversation,
    replyMutationError,
    getMutationLoading,
    getMutationError,
    isDeleteRepliesLoading,
    isReplyMutationLoading,
    isDeleteConversationMutationLoading,
    onDeleteConversationMutationError,
    isLoading: mutationLoading,
    mutationError,
    deleteRepliesError,
    uploadConversationMediaFile,
    getActivityId,
    isUploading,
    progress,
  }
}
